import { useState, useEffect } from 'react';
import { gql, useMutation, useLazyQuery, useQuery } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Hosts } from '~helpers/environments';
import * as authActions from '~actions/auth';
import { formatAuthPayload } from '~helpers/auth';
import generatePath from '~settings/routing';

const CHANGE_CURRENT_ROLE = gql`
  mutation ChangeCurrentRole($input: ChangeCurrentRoleInput) {
    changeCurrentRole(input: $input) {
      authToken
    }
  }
`;

const GET_USER_AUTH = gql`
  query CMS_GetUserAuth {
    identity {
      id
      identity {
        frontendScopes {
          role
          scopes {
            object
            permission
          }
        }
      }
    }
  }
`;

const GET_ROLES_LIST = gql`
  query GET_Roles {
    roles(pageNumber: 1, pageSize: 20, isCmsRole: true) {
      edges {
        node {
          id
          name
          slug
        }
      }
    }
  }
`;

export const useRolesList = () => {
  const { data: roles, loading } = useQuery(GET_ROLES_LIST);
  const [availableRoles, setAvailableRoles] = useState({});

  useEffect(() => {
    let items = roles?.roles?.edges?.map(item => ({ name: item.node.name, slug: item.node.slug }));

    setAvailableRoles(items);
  }, [roles]);

  return [availableRoles, loading];
};

export const handleSwitchRoleRedirect = (history, pathname, currentRole) => {
  if (currentRole === 'agent_admin') {
    window.location.href = `${Hosts.agentUrl}/dashboard`;
    return false;
  } else {
    if (
      pathname.includes('account/account-management') &&
      currentRole === 'content_manager_level_2'
    ) {
      return history.push(generatePath('myProfile', {}));
    } else if (currentRole === 'financial' && !pathname?.includes('billing')) {
      return history.push(generatePath('billing', {}));
    } else if (currentRole === 'agent_admin') {
      return history.push(generatePath('agent', {}));
    } else if (['admin'].includes(currentRole)) {
      return history.push(generatePath('properties', {}));
    } else if (['bd', 'bd_admin', 'regional_bd_head'].includes(currentRole)) {
      return history.push(generatePath('referral.partner', {}));
    } else if (currentRole === 'content_manager') {
      return history.push(generatePath('properties', {}));
    } else if (currentRole === 'bc') {
      return history.push(generatePath('students', {}));
    }
  }
  // }
};

export const useCurrentRoles = () => {
  const { userRoles, authRole } = useSelector(state => ({
    authRole: state?.auth.get('auth').currentRoleSlug,
    userRoles: state?.dashboard.account.get('account')?.toJS()?.userRoles,
  }));
  return { userRoles, authRole };
};

const useSwitchRole = ({ onClose }) => {
  // 1. get authToken by roleSlug
  // 2. use token to request identity
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const { pathname } = useLocation();
  const { userRoles, authRole } = useCurrentRoles();
  const [currentRole, setCurrentRole] = useState(null);

  const [changeRoleMutation, { loading: changeLoading, data: changeData }] = useMutation(
    CHANGE_CURRENT_ROLE,
    {
      onCompleted: result => {
        if (result?.changeCurrentRole?.authToken) {
          queryUserIdentity();
        }
      },
    },
  );

  const setSelectRole = payload => {
    const token = changeData?.changeCurrentRole?.authToken;
    dispatch(authActions.loggedIn(null, token));
    dispatch(authActions.setSelectRole(payload));
  };

  const [queryUserIdentity, { loading: queryLoading }] = useLazyQuery(GET_USER_AUTH, {
    fetchPolicy: 'no-cache',
    context: {
      headers: {
        Authorization: `Bearer ${changeData?.changeCurrentRole?.authToken}`,
      },
    },
    onCompleted: result => {
      setSelectRole(formatAuthPayload(result, currentRole));
      onClose();

      message.success(
        t('cms.switch_role_modal.success_message', {
          role: t(`cms.switch_role_modal.role.${currentRole}`),
        }),
      );

      handleSwitchRoleRedirect(history, pathname, currentRole);
    },
  });

  const handleConfirmSwitchRole = () => {
    if (authRole === currentRole) {
      return false;
    }

    changeRoleMutation({
      variables: {
        input: {
          roleSlug: currentRole,
        },
      },
    });
  };

  return {
    loading: changeLoading || queryLoading,
    authRole,
    currentRole,
    setCurrentRole,
    userRoles,
    handleConfirmSwitchRole,
    useCurrentRoles,
  };
};

export default useSwitchRole;
